import colorPallete, { bodyFonts, elevation, headlineFonts, labelFonts } from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Img from "@insite/mobius/Img/Img";
import { GridContainer, GridItem } from "@insite/mobius/index";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

// this is used to help ensure that the names match between props.fields and definition.fieldDefinitions[].name
const enum fields {
    showFreeDeliveryMessage = "showFreeDeliveryMessage",
    showSecureCheckoutMessage = "showSecureCheckoutMessage",
    showStockMaterialMessage = "showStockMaterialMessage",
    showOrderGuaranteeMessage = "showOrderGuaranteeMessage",
    freeDeliveryMessageIcon = "freeDeliveryMessageIcon",
    secureCheckoutMessageIcon = "secureCheckoutMessageIcon",
    stockMaterialMessageIcon = "stockMaterialMessageIcon",
    orderGuaranteeMessageIcon = "orderGuaranteeMessageIcon",
    freeDeliveryMessageIconAltText = "freeDeliveryMessageIconAltText", 
    secureCheckoutMessageIconAltText = "secureCheckoutMessageIconAltText",
    stockMaterialMessageIconAltText = "stockMaterialMessageIconAltText",
    orderGuaranteeMessageIconAltText = "orderGuaranteeMessageIconAltText",
    freeDeliveryMessageContent = "freeDeliveryMessageContent",
    secureCheckoutMessageContent = "secureCheckoutMessageContent",
    stockMaterialMessageContent = "stockMaterialMessageContent",
    orderGuaranteeMessageContent = "orderGuaranteeMessageContent",
}

const mapStateToProps = (state: ApplicationState) => ({
    isUtilityNationalCustomerType: state.context.session?.properties["isUtilityNationalCustomerType"] === "True",
});

type Props = OwnProps & HasProduct & ReturnType<typeof mapStateToProps>;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showFreeDeliveryMessage]: boolean;
        [fields.showSecureCheckoutMessage]: boolean;
        [fields.showStockMaterialMessage]: boolean;
        [fields.showOrderGuaranteeMessage]: boolean;
        [fields.freeDeliveryMessageIcon]: string;
        [fields.secureCheckoutMessageIcon]: string;
        [fields.stockMaterialMessageIcon]: string;
        [fields.orderGuaranteeMessageIcon]: string;
        [fields.freeDeliveryMessageIconAltText]: string;
        [fields.secureCheckoutMessageIconAltText]: string;
        [fields.stockMaterialMessageIconAltText]: string;
        [fields.orderGuaranteeMessageIconAltText]: string;
        [fields.freeDeliveryMessageContent]: string;
        [fields.secureCheckoutMessageContent]: string;
        [fields.stockMaterialMessageContent]: string;
        [fields.orderGuaranteeMessageContent]: string;
    };
}

export interface DsgCommonOrderSummaryMarketingContentStyles {
    messageGridContainer?: GridContainerProps;
    freeDeliveryGridItem?: GridItemProps;
    secureCheckoutGridItem?: GridItemProps;
    stockMaterialGridItem?: GridItemProps;
    orderGuaranteeGridItem?: GridItemProps;
    wrapper?: InjectableCss;
    msgContent?: InjectableCss;
    imgWrapper?: InjectableCss;
}

export const dsgCommonOrderSummaryMarketingContentStyles: DsgCommonOrderSummaryMarketingContentStyles = {
    messageGridContainer: {
        gap: 16,
        css: css`
            padding-top: 8px;
            [class*="GridItem"] {
                display: flex;
                column-gap: 8px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-top: 32px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    freeDeliveryGridItem: {
        width: 12,
    },
    secureCheckoutGridItem: {
        width: 12,
    },
    stockMaterialGridItem: {
        width: 12,
    },
    orderGuaranteeGridItem: {
        width: 12,
    },
    wrapper: {
        css: css``,
    },
    msgContent: {
        css: css`
            min-height: 24px;
            align-items: center;
            display: flex;
            flex-wrap: wrap;
            &,
            p {
                font: ${labelFonts.label1Regular.fontWeight} ${labelFonts.label1Regular.fontSize} /
                    ${labelFonts.label1Regular.lineHeight} ${labelFonts.label1Regular.fontFamily};
                color: ${colorPallete.grey800};
            }
            strong {
                font-weight: 600;
            }
            p {
                margin-bottom: 0;
            }
            ul,
            ol,
            li {
                margin: 0;
            }
            ul,
            ol {
                padding-left: 24px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            &,
                            p {
                                font-size: ${bodyFonts.body2RegularLg.fontSize};
                                line-height: ${bodyFonts.body2RegularLg.lineHeight};
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    imgWrapper: {
        css: css`
            min-width: 24px;
            max-height: 24px;
        `,
    },
};

const styles = dsgCommonOrderSummaryMarketingContentStyles;

const DsgCommonOrderSummaryMarketingContent: React.FC<Props> = props => {
    return (
        <>
            <StyledWrapper {...styles.wrapper}>
                <GridContainer {...styles.messageGridContainer} className="messageGridContainer">
                    {!props.isUtilityNationalCustomerType && props.fields.showFreeDeliveryMessage && (
                        <GridItem {...styles.freeDeliveryGridItem}>
                            <StyledWrapper {...styles.imgWrapper}>
                                <Img src={props.fields.freeDeliveryMessageIcon} 
                                    altText={props.fields.freeDeliveryMessageIconAltText || ''} />
                            </StyledWrapper>
                            <StyledWrapper {...styles.msgContent}>
                                {parse(props.fields.freeDeliveryMessageContent, parserOptions)}
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {props.fields.showSecureCheckoutMessage && (
                        <GridItem {...styles.secureCheckoutGridItem}>
                            <StyledWrapper {...styles.imgWrapper}>
                                <Img src={props.fields.secureCheckoutMessageIcon} 
                                    altText={props.fields.secureCheckoutMessageIconAltText || ''}/>
                            </StyledWrapper>
                            <StyledWrapper {...styles.msgContent}>
                                {parse(props.fields.secureCheckoutMessageContent, parserOptions)}
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {props.fields.showStockMaterialMessage && (
                        <GridItem {...styles.stockMaterialGridItem}>
                            <StyledWrapper {...styles.imgWrapper}>
                                <Img src={props.fields.stockMaterialMessageIcon} 
                                    altText={props.fields.stockMaterialMessageIconAltText || ''}/>
                            </StyledWrapper>
                            <StyledWrapper {...styles.msgContent}>
                                {parse(props.fields.stockMaterialMessageContent, parserOptions)}
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {props.fields.showOrderGuaranteeMessage && (
                        <GridItem {...styles.orderGuaranteeGridItem}>
                            <StyledWrapper {...styles.imgWrapper}>
                                <Img src={props.fields.orderGuaranteeMessageIcon} 
                                    altText={props.fields.orderGuaranteeMessageIconAltText || ''} />
                            </StyledWrapper>
                            <StyledWrapper {...styles.msgContent}>
                                {parse(props.fields.orderGuaranteeMessageContent, parserOptions)}
                            </StyledWrapper>
                        </GridItem>
                    )}
                </GridContainer>
            </StyledWrapper>
        </>
    );
};

const definition: WidgetDefinition = {
    group: "Common",
    fieldDefinitions: [
        {
            name: fields.showFreeDeliveryMessage,
            displayName: "Show Free Delivery Message",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "Contextual",
            sortOrder: 1,
        },
        {
            fieldType: "Contextual",
            name: fields.freeDeliveryMessageIcon,
            editorTemplate: "ImagePickerField",
            defaultValue: "/UserFiles/TruckIcon.png",
            sortOrder: 2,
            isVisible: widget => widget.fields.showFreeDeliveryMessage,
        },
        {
            fieldType: "General",
            name: fields.freeDeliveryMessageIconAltText,
            editorTemplate: "TextField",
            defaultValue: "",
            sortOrder: 2,
            isVisible: widget => widget.fields.showFreeDeliveryMessage,
        },
        {
            name: fields.freeDeliveryMessageContent,
            displayName: "Free Delivery Message Content",
            editorTemplate: "RichTextField",
            defaultValue:
                "<p><strong>FREE</strong> delivery available on most items when delivered direct from DSG.</p>",
            fieldType: "Contextual",
            sortOrder: 3,
            extendedConfig: { height: 100 },
            isVisible: widget => widget.fields.showFreeDeliveryMessage,
            expandedToolbarButtons: {
                moreMisc: {
                    buttons: [
                        "insertLink",
                        "insertImage",
                        "insertHR",
                        "specialCharacters",
                        "insertVideo",
                        "insertFile",
                    ],
                    buttonsVisible: 3,
                },
                code: {
                    buttons: ["selectAll", "html", "help"],
                    buttonsVisible: 3,
                },
            },
        },
        {
            name: fields.showSecureCheckoutMessage,
            displayName: "Show Secure Checkout Message",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "Contextual",
            sortOrder: 4,
        },
        {
            fieldType: "Contextual",
            name: fields.secureCheckoutMessageIcon,
            editorTemplate: "ImagePickerField",
            defaultValue: "/UserFiles/SecureIcon.png",
            sortOrder: 5,
            isVisible: widget => widget.fields.showSecureCheckoutMessage,
        },
        {
            fieldType: "General",
            name: fields.secureCheckoutMessageIconAltText,
            editorTemplate: "TextField",
            defaultValue: "",
            sortOrder: 5,
            isVisible: widget => widget.fields.showSecureCheckoutMessage,
        },
        {
            name: fields.secureCheckoutMessageContent,
            displayName: "Secure Checkout Message Content",
            editorTemplate: "RichTextField",
            defaultValue: "<p>Secure checkout</p>",
            fieldType: "Contextual",
            sortOrder: 6,
            extendedConfig: { height: 100 },
            isVisible: widget => widget.fields.showSecureCheckoutMessage,
            expandedToolbarButtons: {
                moreMisc: {
                    buttons: [
                        "insertLink",
                        "insertImage",
                        "insertHR",
                        "specialCharacters",
                        "insertVideo",
                        "insertFile",
                    ],
                    buttonsVisible: 3,
                },
                code: {
                    buttons: ["selectAll", "html", "help"],
                    buttonsVisible: 3,
                },
            },
        },
        {
            name: fields.showStockMaterialMessage,
            displayName: "Show Stock Material Message",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "Contextual",
            sortOrder: 7,
        },
        {
            fieldType: "Contextual",
            name: fields.stockMaterialMessageIcon,
            editorTemplate: "ImagePickerField",
            defaultValue: "/UserFiles/StockMaterialIcon.png",
            sortOrder: 8,
            isVisible: widget => widget.fields.showStockMaterialMessage,
        },
        {
            fieldType: "General",
            name: fields.stockMaterialMessageIconAltText,
            editorTemplate: "TextField",
            defaultValue: "",
            sortOrder: 8,
            isVisible: widget => widget.fields.showStockMaterialMessage,
        },
        {
            name: fields.stockMaterialMessageContent,
            displayName: "Stock Material Message Content",
            editorTemplate: "RichTextField",
            defaultValue: `
                <p><strong>STOCK MATERIAL:</strong> There is not a restocking charge for material returned within 30 days of the invoice date. You will be charged a restocking fee for material returned beyond 30 days as follows:</p>

                <ul>
                    <li>

                        <p>Returned from 30 to 90 days of invoice date: 10% restocking charge.</p>
                    </li>
                    <li>

                        <p>Returned from 90 to 120 days of invoice date: 25% restocking charge.</p>
                    </li>
                </ul>

            `,
            fieldType: "Contextual",
            sortOrder: 9,
            extendedConfig: { height: 100 },
            isVisible: widget => widget.fields.showStockMaterialMessage,
            expandedToolbarButtons: {
                moreMisc: {
                    buttons: [
                        "insertLink",
                        "insertImage",
                        "insertHR",
                        "specialCharacters",
                        "insertVideo",
                        "insertFile",
                    ],
                    buttonsVisible: 3,
                },
                code: {
                    buttons: ["selectAll", "html", "help"],
                    buttonsVisible: 3,
                },
            },
        },
        {
            name: fields.showOrderGuaranteeMessage,
            displayName: "Show Order Guarantee Message",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "Contextual",
            sortOrder: 11,
        },
        {
            fieldType: "Contextual",
            name: fields.orderGuaranteeMessageIcon,
            editorTemplate: "ImagePickerField",
            defaultValue: "/UserFiles/GuaranteeIcon.png",
            sortOrder: 12,
            isVisible: widget => widget.fields.showOrderGuaranteeMessage,
        },
        {
            fieldType: "General",
            name: fields.orderGuaranteeMessageIconAltText,
            editorTemplate: "TextField",
            defaultValue: "",
            sortOrder: 12,
            isVisible: widget => widget.fields.showOrderGuaranteeMessage,
        },
        {
            name: fields.orderGuaranteeMessageContent,
            displayName: "Order Guarantee Message Content",
            editorTemplate: "RichTextField",
            defaultValue: "<p>100% order accuracy guarantee</p>",
            fieldType: "Contextual",
            isRequired: false,
            sortOrder: 13,
            extendedConfig: { height: 100 },
            isVisible: widget => widget.fields.showOrderGuaranteeMessage,
            expandedToolbarButtons: {
                moreMisc: {
                    buttons: [
                        "insertLink",
                        "insertImage",
                        "insertHR",
                        "specialCharacters",
                        "insertVideo",
                        "insertFile",
                    ],
                    buttonsVisible: 3,
                },
                code: {
                    buttons: ["selectAll", "html", "help"],
                    buttonsVisible: 3,
                },
            },
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(DsgCommonOrderSummaryMarketingContent)),
    definition,
};

export default widgetModule;
